import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, ArrowRight } from "lucide-react";
import axios from "axios";
import { Lightbox } from "./Lightbox";

const WA_NUMBER = "918097237982";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const logWaClick = (payload) => {
  try {
    axios.post(`${API}/wa-click`, payload).catch(() => {});
  } catch (_) {
    /* ignore */
  }
};

/**
 * Full-screen project detail modal.
 * Props: project (from PROJECTS data), onClose()
 */
export const ProjectDetail = ({ project, onClose }) => {
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && lightboxIdx === -1) onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, lightboxIdx]);

  if (!project) return null;

  const images = project.images || [];
  const hero = project.hero || project.src || images[0];

  const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    `Hi FOXEL, I'd like to enquire about "${project.title} — ${project.place}".`
  )}`;

  return (
    <AnimatePresence>
      <motion.div
        key="pd-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
        data-testid="project-detail"
      >
        <motion.div
          key="pd-panel"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="max-w-6xl mx-auto my-6 md:my-12 bg-[#FDFCFD] rounded-3xl border border-[#E5DEE5] shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="header-gradient text-white px-6 md:px-10 py-5 md:py-6 flex items-start justify-between gap-4 sticky top-0 z-10">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl leading-tight">
                  {project.title}
                </h2>
                {project.tag && (
                  <span className="rounded-full bg-white/15 border border-white/30 text-white text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1">
                    {project.tag}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-white/80 text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
                <MapPin size={14} />
                <span>{project.place}</span>
              </div>
            </div>
            <button
              data-testid="pd-close"
              onClick={onClose}
              className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-[#4a1c40] transition-colors duration-200 shrink-0"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-5 md:px-10 py-8 md:py-10">
            {/* Hero */}
            {hero && (
              <button
                onClick={() => setLightboxIdx(images.indexOf(hero) >= 0 ? images.indexOf(hero) : 0)}
                className="block w-full group overflow-hidden rounded-2xl border border-[#E5DEE5]"
              >
                <img
                  src={hero}
                  alt={project.title}
                  className="w-full h-auto object-cover aspect-[16/9] transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </button>
            )}

            {/* Brief + materials */}
            <div className="mt-10 grid md:grid-cols-5 gap-8 md:gap-12">
              <div className="md:col-span-3">
                <span className="text-[#c85a54] text-[11px] tracking-[0.3em] uppercase font-semibold">
                  Project Brief
                </span>
                <p className="mt-4 text-[#1A1518] text-base md:text-lg leading-relaxed">
                  {project.brief}
                </p>
                <p className="mt-4 text-[#4D454A] italic">{project.caption}</p>
              </div>
              <div className="md:col-span-2">
                <span className="text-[#c85a54] text-[11px] tracking-[0.3em] uppercase font-semibold">
                  Material Palette
                </span>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {(project.materials || []).map((m) => (
                    <div key={m.label} className="flex items-center gap-3 rounded-xl border border-[#E5DEE5] p-3">
                      <span
                        className="w-10 h-10 rounded-lg shrink-0 border border-black/5"
                        style={{ background: m.color }}
                      />
                      <div className="min-w-0">
                        <div className="text-[#1A1518] text-sm font-semibold leading-tight truncate">
                          {m.label}
                        </div>
                        <div className="text-[#8a828a] text-[10px] tracking-widest uppercase">
                          {m.color}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Full render set */}
            {images.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-[#c85a54] text-[11px] tracking-[0.3em] uppercase font-semibold">
                    Full Render Set
                  </span>
                  <div className="h-px flex-1 bg-[#E5DEE5]" />
                  <span className="text-[#8a828a] text-[10px] tracking-widest uppercase">
                    {images.length} images
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setLightboxIdx(i)}
                      data-testid={`pd-img-${i}`}
                      className="group relative overflow-hidden rounded-2xl border border-[#E5DEE5]"
                    >
                      <img
                        src={img}
                        alt={`${project.title} ${i + 1}`}
                        loading="lazy"
                        className="w-full h-auto object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <span className="absolute bottom-3 right-3 rounded-full bg-white/90 text-[#1A1518] text-[10px] tracking-widest uppercase font-bold px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Expand
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-14 rounded-2xl brand-gradient p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-white">
                <div className="text-[11px] tracking-[0.3em] uppercase font-semibold text-white/80">
                  Interested in a project like this?
                </div>
                <div className="font-display font-bold text-white text-lg md:text-xl mt-1">
                  Let&apos;s design your version of {project.title}.
                </div>
              </div>
              <a
                data-testid="pd-whatsapp"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  logWaClick({
                    source: "project-detail",
                    project_id: project.id,
                    project_title: project.title,
                    referrer: window.location.pathname,
                  })
                }
                className="group inline-flex items-center gap-2 rounded-full bg-white text-[#4a1c40] hover:bg-[#1A1518] hover:text-white transition-colors duration-300 uppercase tracking-widest text-xs font-bold px-6 py-3.5"
              >
                Enquire on WhatsApp
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Lightbox layered on top */}
      {lightboxIdx >= 0 && (
        <Lightbox
          images={images}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onNav={setLightboxIdx}
        />
      )}
    </AnimatePresence>
  );
};
